import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'can-you-send-a-sar-to-a-foster-carer';

export const CanYouSarAFosterCarer = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        Short answer: sending a Subject Access Request straight to a former foster carer, as an
        individual, is not a reliable route, and in most cases you do not need to try it. But
        that is a different question from whether what a foster carer wrote about you is
        recoverable — it almost always is, just not from them directly. This guide explains why,
        and what to actually do about it.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Correcting a common assumption" title="Foster carers are not council employees">
      <p>
        It is a reasonable assumption, but it is not correct, and it matters for how this works.
        A Court of Appeal ruling in 1998 held that foster carers do not work under a contract of
        employment with the local authority — their relationship is governed by fostering
        legislation and regulations instead, which makes them, in law, closer to independent
        contractors than employees. This was tested again as recently as January 2025, when an
        Employment Tribunal confirmed foster carers are still neither employees nor "workers" in
        the ordinary sense, even though it found a separate route for some claims under human
        rights law.
      </p>
      <p>
        That might sound like it settles the question in the carer&apos;s favour — no employment
        relationship, so surely nothing they wrote belongs to the council. It doesn&apos;t, because
        employment status was never actually the test that matters here.
      </p>
    </GuideSection>

    <GuideSection eyebrow="What actually matters" title="It's about who controls the data, not who employs the carer">
      <p>
        Data protection law asks a different question from employment law: who is the{' '}
        <strong className="text-ink">data controller</strong> — the party who decides why
        information is being processed. Approved foster carers keep records about the children
        placed with them because the fostering service requires it, not because they personally
        chose to. The{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2011/581/contents/made">
          Fostering Services (England) Regulations 2011
        </SourceLink>{' '}
        put the statutory duty to maintain records on the{' '}
        <strong className="text-ink">fostering service provider</strong> — the council or agency
        — not the individual carer, and the accompanying{' '}
        <SourceLink href="https://www.gov.uk/government/publications/fostering-services-national-minimum-standards">
          national minimum standards
        </SourceLink>{' '}
        expect carers to keep the fostering service informed, including through the kind of daily
        or weekly logs many carers are asked to submit.
      </p>
      <p>
        That means a diary, log or report a carer keeps about you{' '}
        <strong className="text-ink">for the purpose of fostering you</strong> — to report back,
        for a review, in case something needs to be flagged — is the fostering service&apos;s
        material, not the carer&apos;s own purely personal activity. It exists because of a
        regulated, supervised role the council or agency is legally responsible for, which is
        exactly the kind of connection to a "professional or commercial activity" that takes it
        outside the narrow exemption individuals otherwise get for personal record-keeping. This
        is material the council or agency is responsible for producing, whether or not it
        currently holds a physical copy, and it is worth being firm about that if a council tries
        to suggest otherwise.
      </p>
      <p>
        There is good supporting authority for taking that firm line, even though it comes from a
        different area of law. In{' '}
        <SourceLink href="https://www.supremecourt.uk/cases/uksc-2016-0004">
          Armes v Nottinghamshire County Council
        </SourceLink>
        , the Supreme Court held a council{' '}
        <strong className="text-ink">vicariously liable</strong> for abuse committed by foster
        carers — despite the carers not being employees — because foster carers provide care as{' '}
        <strong className="text-ink">"an integral part of the local authority&apos;s child care
        services,"</strong> recruited, trained, supervised, approved and paid by the council,
        which exercises significant control over them throughout. That is a case about abuse, not
        data protection, so it does not directly decide this question — but the same reasoning
        cuts hard against any suggestion that a carer&apos;s fostering-related writing is somehow
        separate from the council&apos;s own operation. If a foster placement is integral to the
        council&apos;s child care service for the purpose of making it liable for what goes wrong
        in it, a council is on weak ground arguing the same placement is somehow the carer&apos;s
        own private affair for the purpose of avoiding your Subject Access Request.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>Push for the more personal-feeling material too — a private diary, a family
        Christmas newsletter mentioning you — rather than assuming it is out of reach.</strong>{' '}
        The UK GDPR does carve out information an individual processes purely for personal or
        household purposes, with no connection to a professional or commercial activity, from data
        protection law entirely. But you would only ever be in this carer&apos;s life, or in their
        family&apos;s Christmas letter, because the council placed you there and approved and
        supervised that placement — the connection to the regulated fostering arrangement is not
        incidental, it is the entire reason the writing exists at all. That is a genuinely strong
        argument for treating it as within scope, not a stretch. What is honestly true is that no
        court or ICO decision has tested this exact scenario, so a council could push back — but
        that is a reason to make the argument clearly and ask them to justify a refusal, not a
        reason to assume in advance that you will not get it.
      </p>
    </GuideNote>

    <GuideCTA
      heading="Add your foster carer's name and we'll ask on your behalf"
      body="When you add a placement to Precious Data, we ask for the foster carer's name — and include it directly in your letter to the council or agency, so they know exactly who to check with, not just which organisation to search."
    />

    <GuideSection eyebrow="So who do you actually ask?" title="Push the council or agency — not the carer">
      <p>
        Even with a strong argument behind you, sending a SAR to a former foster carer directly is
        still a weak strategy in practice, for reasons beyond the legal question:
      </p>
      <GuideChecklist
        items={[
          "You may not know how to contact them, and organisations are not obliged to hand over a former carer's personal contact details as part of your own SAR.",
          "An individual former carer, years after a placement ended, is far less likely to handle a formal request correctly, keep track of a deadline, or know how to apply the third-party exemptions properly, than a council or agency with a data protection team.",
          'Even a strong argument is easier to win against an organisation with its own legal advice and a duty to justify its decisions than against an individual you would have to chase and persuade on your own.',
        ]}
      />
      <p>
        The reliable route is to make your Subject Access Request to the council or fostering
        agency responsible for the placement — see our{' '}
        <Link to="/guides/how-to-request-your-fostering-records" className="text-brand underline hover:opacity-90">
          guide to requesting your fostering records
        </Link>{' '}
        — and be specific and direct about the carer. Name them, give the approximate dates of
        the placement, and explicitly ask the organisation to check with the carer (or their own
        records of what the carer submitted) for everything relevant — logs, diaries, photographs,
        memory books, and any family correspondence that mentions you — not only the formal case
        file. If they push back on anything the carer wrote as "not ours to give," it is
        reasonable to ask them directly to explain why a placement they arranged, approved and
        supervised should be treated as outside their own responsibility.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="foster-carer-sar-faq">
        <GuideFaqItem
          q="What if the council says the carer's diary was never handed over to them?"
          a="Ask them to request it. A carer's day-to-day recording about a child in their care exists because of their approved, supervised role, and the fostering service is entitled — and often expected under the national minimum standards — to ask an approved carer for it, past or present. The council not currently holding a copy is a reason to ask, not a reason to give up."
        />
        <GuideFaqItem
          q="Can I ask my former foster carer myself, informally, outside a SAR?"
          a="Yes, and for a long-term carer you're still in contact with, that's often simpler and kinder than any formal process — many carers keep photos, memory books or letters they'd be glad to share directly. A SAR is the fallback for when that informal route isn't available or doesn't work, not the only option."
        />
        <GuideFaqItem
          q="Does it matter if the carer was paid a fostering allowance or fee?"
          a="It supports your position rather than weakening it. The Supreme Court's reasoning in Armes v Nottinghamshire County Council specifically pointed to councils recruiting, training, supervising and paying foster carers as part of why fostering counts as an integral part of the council's own child care service, not a separate private arrangement — the same facts that make a placement 'the council's' for that purpose make it much harder to call a carer's related record-keeping purely personal for a data protection one."
        />
        <GuideFaqItem
          q="I was in a children's home, not with a foster carer. Does any of this apply?"
          a="Not in the same way — residential staff in a children's home are ordinary employees of the home or organisation running it, so records they wrote are unambiguously that organisation's, with no equivalent 'not an employee' question to untangle. See our guide to requesting your fostering records for how residential and foster placements differ."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The employment-status position is summarised well by{' '}
        <SourceLink href="https://wslaw.co.uk/blog/foster-caring-where-does-it-fall-the-sphere-of-family-or-work/">
          Winckworth Sherwood&apos;s analysis
        </SourceLink>{' '}
        of the January 2025 Employment Tribunal decision and the 1998 Court of Appeal ruling it
        was bound by. The Supreme Court&apos;s reasoning on councils&apos; control over foster
        carers is set out in{' '}
        <SourceLink href="https://www.supremecourt.uk/cases/uksc-2016-0004">
          Armes v Nottinghamshire County Council [2017] UKSC 60
        </SourceLink>
        , including its official press summary. The record-keeping duty sits in the{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2011/581/contents/made">
          Fostering Services (England) Regulations 2011
        </SourceLink>{' '}
        and the{' '}
        <SourceLink href="https://www.gov.uk/government/publications/fostering-services-national-minimum-standards">
          national minimum standards for fostering
        </SourceLink>
        . The personal/household carve-out from data protection law is explained in the ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/exemptions/a-guide-to-the-data-protection-exemptions/">
          guide to the data protection exemptions
        </SourceLink>
        , under "domestic purposes."
      </p>
    </GuideSection>

    <GuideCTA
      heading="Never lose track of who to ask"
      body="Precious Data keeps every placement, and every carer you tell us about, attached to the right organisation — so when you request your fostering records, nothing about who actually looked after you gets left out of the letter."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default CanYouSarAFosterCarer;
